import { Collapse, Group, Stack as MantineStack, Stack, Switch } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { useEpics } from '../../../api/process-client/process-client.hooks';
import { useGetWorkflows } from '../../../api/workflows-client/workflows.client.hooks';
import { FilterPanel } from '../../../components/filters/filter-panel';
import type { FilterGroup } from '../../../components/filters/filter-panel.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { secondaryBase } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Select } from '../../../ui-library/select/select';
import { Text } from '../../../ui-library/typography/typography';
import { TaskType, type TaskTypeValue } from '../../tasks-table/tasks-table.type';
import { ProcessViewMode } from '../process.type';
import {
  createProcessFilterGroups,
  createResetFiltersHandler,
  createWorkflowSelectHandler,
} from './process-filter-helpers';
import { TaskSelect } from './task-select';

type Props = {
  leftTaskTypes: string[];
  rightTaskTypes: string[];
};

export function ProcessV2FilterPanel({ leftTaskTypes, rightTaskTypes }: Props) {
  const organizationId = useGlobalStore((state) => state.organization)?.id || '';
  const portfolioId = useGlobalStore((state) => state.portfolio)?.id || null;
  const { scope, setScope, isComparisonView, setIsComparisonView, rightScope, setRightScope } = useProcessStore();

  // Destructure values from scope for easier use
  const { taskType, epic, portfolio, team, board, useWorkflow, workflow, startDate, endDate } = scope;

  // Refs to track all seen task types for both sides
  const leftAllSeenTaskTypesRef = useRef<Set<string>>(new Set(Object.values(TaskType)));
  const rightAllSeenTaskTypesRef = useRef<Set<string>>(new Set(Object.values(TaskType)));
  const [leftForceRender, setLeftForceRender] = useState(0);
  const [rightForceRender, setRightForceRender] = useState(0);

  // State for filter groups
  const [isLeftOpen, { toggle: toggleLeftOpen }] = useDisclosure(false);
  const [isRightOpen, { toggle: toggleRightOpen }] = useDisclosure(false);
  const [leftFilterGroups, setLeftFilterGroups] = useState<FilterGroup[]>([]);
  const [rightFilterGroups, setRightFilterGroups] = useState<FilterGroup[]>([]);
  const [viewMode, setViewMode] = useState<ProcessViewMode>(
    isComparisonView ? ProcessViewMode.COMPARISON : ProcessViewMode.SINGLE,
  );

  const handleToggleComparisonView = useCallback(() => {
    // First update view mode which is local state
    const newComparisonMode = !isComparisonView;
    setViewMode(newComparisonMode ? ProcessViewMode.COMPARISON : ProcessViewMode.SINGLE);

    // Then update the store
    setIsComparisonView(newComparisonMode);
  }, [isComparisonView, setIsComparisonView]);

  const { data: workflows } = useGetWorkflows(organizationId, portfolioId);

  // Get teams from global store
  const teams = useGlobalStore((state) => state.teams || []);

  // Update seen task types when leftTaskTypes changes
  useEffect(() => {
    if (leftTaskTypes?.length) {
      let updated = false;
      leftTaskTypes.forEach((type) => {
        if (!leftAllSeenTaskTypesRef.current.has(type)) {
          leftAllSeenTaskTypesRef.current.add(type);
          updated = true;
        }
      });

      if (updated) {
        setLeftForceRender((prev) => prev + 1);
      }
    }
  }, [leftTaskTypes]);

  // Update seen task types when rightTaskTypes changes
  useEffect(() => {
    if (rightTaskTypes?.length) {
      let updated = false;
      rightTaskTypes.forEach((type) => {
        if (!rightAllSeenTaskTypesRef.current.has(type)) {
          rightAllSeenTaskTypesRef.current.add(type);
          updated = true;
        }
      });

      if (updated) {
        setRightForceRender((prev) => prev + 1);
      }
    }
  }, [rightTaskTypes]);

  // Fetch epics for the left section
  const { epics: leftEpics = [] } = useEpics(
    {
      portfolioId: portfolio?.id || null,
      projectId: team?.id || null,
      subprojectId: board?.id || null,
    },
    {
      queryKey: ['epics', portfolio?.id, team?.id, board?.id],
      enabled: Boolean(team?.id),
      staleTime: 1000 * 60 * 5,
    },
  );

  // Fetch epics for the right section (only in comparison mode)
  const { epics: rightEpics = [] } = useEpics(
    {
      portfolioId: portfolio?.id || null,
      projectId: rightScope?.team?.id || null,
      subprojectId: rightScope?.board?.id || null,
    },
    {
      queryKey: ['epics-right', portfolio?.id, rightScope?.team?.id, rightScope?.board?.id],
      enabled: Boolean(rightScope?.team?.id) && isComparisonView,
      staleTime: 1000 * 60 * 5,
    },
  );

  // Update board when workflow changes for left side
  useEffect(() => {
    if (useWorkflow && workflow) {
      // If the current board is not in the workflow boards, clear it
      if (board && !workflow.subprojects.includes(board.id)) {
        setScope({ board: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setScope, useWorkflow, workflow, board]); // Intentionally omitting setScope to prevent loops

  // Update board when workflow changes for right side
  useEffect(() => {
    if (rightScope?.useWorkflow && rightScope.workflow) {
      // If the current board is not in the workflow boards, clear it
      if (rightScope.board && !rightScope.workflow.subprojects.includes(rightScope.board.id)) {
        setRightScope({ board: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRightScope, rightScope?.useWorkflow, rightScope?.workflow, rightScope?.board]); // Intentionally omitting setRightScope to prevent loops

  // --------------------------------------------------
  // Left side handlers
  // --------------------------------------------------

  // Handle team selection/deselection
  const handleLeftTeamSelected = useCallback(
    (selectedTeam: any) => {
      setScope({ team: selectedTeam, board: null, epic: null, task: null });
    },
    [setScope],
  );

  // Handle board selection/deselection
  const handleLeftBoardSelected = useCallback(
    (selectedBoard: any) => {
      if (selectedBoard === null) {
        setScope({
          board: selectedBoard,
          epic: null,
          task: null,
          useGroups: true,
        });
      } else {
        setScope({ board: selectedBoard, epic: null, task: null });
      }
    },
    [setScope],
  );

  // Handle epic selection/deselection
  const handleLeftEpicSelected = useCallback(
    (selectedEpic: any) => {
      setScope({ epic: selectedEpic });
    },
    [setScope],
  );

  // Handle task selection/deselection
  const handleLeftTaskSelected = useCallback(
    (selectedTask: any) => {
      setScope({ task: selectedTask });
    },
    [setScope],
  );

  // Handle task type selection/deselection
  const handleLeftTaskTypeSelected = useCallback(
    (selectedTaskType: TaskTypeValue | null) => {
      setScope({ taskType: selectedTaskType });
    },
    [setScope],
  );

  // Handle workflow selection/deselection
  const handleLeftWorkflowChange = useCallback(
    (value: string | null) => {
      if (value) {
        const workflow = workflows?.find((w) => w.id === value);
        setScope({ workflow, board: null, task: null });
      } else {
        // Instead of allowing null, select the first workflow if available
        const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
        if (firstWorkflow) {
          setScope({ workflow: firstWorkflow, board: null, task: null });
        }
      }
    },
    [workflows, setScope],
  );

  // --------------------------------------------------
  // Right side handlers (for comparison view)
  // --------------------------------------------------

  // Handle team selection/deselection for right side
  const handleRightTeamSelected = useCallback(
    (selectedTeam: any) => {
      setRightScope({
        team: selectedTeam,
        board: null,
        epic: null,
        task: null,
      });
    },
    [setRightScope],
  );

  // Handle board selection/deselection for right side
  const handleRightBoardSelected = useCallback(
    (selectedBoard: any) => {
      if (selectedBoard === null) {
        setRightScope({
          board: selectedBoard,
          epic: null,
          task: null,
          useGroups: true,
        });
      } else {
        setRightScope({ board: selectedBoard, epic: null, task: null });
      }
    },
    [setRightScope],
  );

  // Handle epic selection/deselection for right side
  const handleRightEpicSelected = useCallback(
    (selectedEpic: any) => {
      setRightScope({ epic: selectedEpic });
    },
    [setRightScope],
  );

  // Handle task selection/deselection for right side
  const handleRightTaskSelected = useCallback(
    (selectedTask: any) => {
      setRightScope({ task: selectedTask });
    },
    [setRightScope],
  );

  // Handle task type selection/deselection for right side
  const handleRightTaskTypeSelected = useCallback(
    (selectedTaskType: TaskTypeValue | null) => {
      setRightScope({ taskType: selectedTaskType });
    },
    [setRightScope],
  );

  // Handle workflow selection/deselection for right side
  const handleRightWorkflowChange = useCallback(
    (value: string | null) => {
      if (value) {
        const workflow = workflows?.find((w) => w.id === value);
        setRightScope({ workflow, board: null, task: null });
      } else {
        // Instead of allowing null, select the first workflow if available
        const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
        if (firstWorkflow) {
          setRightScope({ workflow: firstWorkflow, board: null, task: null });
        }
      }
    },
    [workflows, setRightScope],
  );

  // Create handlers using helper functions
  const handleLeftWorkflowSelected = createWorkflowSelectHandler(setScope, workflows || [], teams);
  const handleRightWorkflowSelected = createWorkflowSelectHandler(setRightScope, workflows || [], teams);

  const handleLeftResetFilters = createResetFiltersHandler(setScope, useWorkflow, workflows || [], teams);

  const handleRightResetFilters = createResetFiltersHandler(
    setRightScope,
    rightScope?.useWorkflow || false,
    workflows || [],
    teams,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: necessary for this useEffect
  useEffect(() => {
    if (!useWorkflow && !team) {
      setScope({ team: teams?.[0] });
      return;
    }

    const groups = createProcessFilterGroups({
      teams: teams as any[],
      team: team as any,
      board: board as any,
      taskType,
      taskTypes: leftTaskTypes,
      epic,
      epics: leftEpics,
      useWorkflow,
      workflow,
      workflows: workflows || [],
      allSeenTaskTypesRef: leftAllSeenTaskTypesRef,
      onTeamSelected: handleLeftTeamSelected,
      onBoardSelected: handleLeftBoardSelected,
      onTaskTypeSelected: handleLeftTaskTypeSelected,
      onEpicSelected: handleLeftEpicSelected,
      onWorkflowChange: handleLeftWorkflowChange,
    });

    setLeftFilterGroups(groups);
  }, [
    teams,
    team,
    board,
    taskType,
    epic,
    useWorkflow,
    workflow,
    workflows,
    leftTaskTypes,
    leftForceRender,
    handleLeftTeamSelected,
    handleLeftBoardSelected,
    handleLeftTaskTypeSelected,
    handleLeftEpicSelected,
    handleLeftWorkflowChange,
    handleLeftTaskSelected,
    setScope,
  ]);

  useDeepCompareEffect(() => {
    if (!isComparisonView || !rightScope) {
      return;
    }

    if (!rightScope.useWorkflow && !rightScope.team) {
      setRightScope({ team: teams?.[0] });
      return;
    }

    const groups = createProcessFilterGroups({
      teams: teams as any[],
      team: rightScope.team as any,
      board: rightScope.board as any,
      taskType: rightScope.taskType,
      taskTypes: rightTaskTypes,
      epic: rightScope.epic,
      epics: rightEpics,
      useWorkflow: rightScope.useWorkflow,
      workflow: rightScope.workflow,
      workflows: workflows || [],
      allSeenTaskTypesRef: rightAllSeenTaskTypesRef,
      onTeamSelected: handleRightTeamSelected,
      onBoardSelected: handleRightBoardSelected,
      onTaskTypeSelected: handleRightTaskTypeSelected,
      onEpicSelected: handleRightEpicSelected,
      onWorkflowChange: handleRightWorkflowChange,
    });

    setRightFilterGroups(groups);
  }, [
    isComparisonView,
    rightScope,
    teams,
    rightEpics,
    workflows,
    rightTaskTypes,
    rightForceRender,
    handleRightTeamSelected,
    handleRightBoardSelected,
    handleRightTaskTypeSelected,
    handleRightEpicSelected,
    handleRightWorkflowChange,
    setRightScope,
  ]);

  const leftHeaderSection = (
    <MantineStack>
      <Switch
        label="Use Workflows"
        checked={useWorkflow}
        onChange={handleLeftWorkflowSelected}
        color={secondaryBase}
        disabled={!workflows || workflows.length === 0}
      />
    </MantineStack>
  );

  const rightHeaderSection = rightScope && (
    <MantineStack>
      <Switch
        label="Use Workflows"
        checked={rightScope.useWorkflow}
        onChange={handleRightWorkflowSelected}
        color={secondaryBase}
        disabled={!workflows || workflows.length === 0}
      />
    </MantineStack>
  );

  const viewOptions = [
    { value: ProcessViewMode.SINGLE, label: 'Single View' },
    { value: ProcessViewMode.COMPARISON, label: 'Comparison View' },
  ];

  return isComparisonView ? (
    <Stack gap="md">
      <Select value={viewMode} options={viewOptions} onChange={handleToggleComparisonView} />
      <Divider />
      <Group justify="space-between">
        <Text weight="bold">Left Graph</Text>
        <Icon name={isLeftOpen ? 'unfold_less' : 'unfold_more'} size={16} onClick={toggleLeftOpen} />
      </Group>
      <Collapse in={isLeftOpen}>
        <FilterPanel
          filterGroups={leftFilterGroups}
          onResetFilters={handleLeftResetFilters}
          useSelectFilters={true}
          headerSection={leftHeaderSection}
        />
        <TaskSelect
          handleTaskSelected={handleLeftTaskSelected}
          startDate={startDate}
          endDate={endDate}
          hideLabel={false}
        />
      </Collapse>
      <Divider />
      <Group justify="space-between">
        <Text weight="bold">Right Graph</Text>
        <Icon name={isRightOpen ? 'unfold_less' : 'unfold_more'} size={16} onClick={toggleRightOpen} />
      </Group>
      <Collapse in={isRightOpen}>
        <FilterPanel
          filterGroups={rightFilterGroups}
          onResetFilters={handleRightResetFilters}
          useSelectFilters={true}
          headerSection={rightHeaderSection}
        />
        <TaskSelect
          handleTaskSelected={handleRightTaskSelected}
          startDate={startDate}
          endDate={endDate}
          isRightSide={true}
          hideLabel={false}
        />
      </Collapse>
    </Stack>
  ) : (
    <>
      <FilterPanel
        options={viewOptions}
        selectedOption={viewMode}
        onOptionChange={handleToggleComparisonView}
        filterGroups={leftFilterGroups}
        onResetFilters={handleLeftResetFilters}
        useSelectFilters={true}
        headerSection={leftHeaderSection}
      />
      <TaskSelect handleTaskSelected={handleLeftTaskSelected} startDate={startDate} endDate={endDate} />
    </>
  );
}
